/**
 * useTradeModal — Trade modal state, order construction, and submission.
 *
 * Manages tradeReq/tradeError state, builds PlaceOrderRequest from click data,
 * and handles fire-and-forget order submission for both Kalshi and Polymarket.
 */

import { useState, useCallback, useMemo } from 'react';
import type { Sport } from '@/lib/sportsDiscovery';
import type { TradeClickRequest } from '@/components/nba-value-dashboard/tradeTypes';
import type {
  PlaceOrderRequest,
  PlaceOrderSubmission,
} from '@/components/nba-value-dashboard/PlaceOrderModal';
import type { ConsolidatedGameBooks } from '@/lib/nbaConsolidated/types';

interface UseTradeModalParams {
  selectedSport: Sport;
  /** Filtered moneyline games for current view (used for slate time range) */
  moneylineGamesForView: ConsolidatedGameBooks[];
  onPlaceKalshiOrder?: (args: {
    marketTicker: string;
    side: 'yes' | 'no';
    priceCents: number;
    contracts: number;
    isTakerMode: boolean;
    expirationTs?: number;
  }) => Promise<void>;
  onPlacePolymarketOrder?: (args: {
    tokenId: string;
    conditionId?: string;
    action: 'buy' | 'sell';
    priceCents: number;
    contracts: number;
    isTakerMode: boolean;
    tickSize?: string;
    negRisk?: boolean;
  }) => Promise<void>;
}

export interface TradeModalResult {
  tradeReq: TradeClickRequest | null;
  tradeError: string | null;
  tradeSubmitting: boolean;
  placeOrderRequest: PlaceOrderRequest | null;
  isOpen: boolean;
  openTrade: (req: TradeClickRequest) => void;
  closeTrade: () => void;
  handleSubmitTrade: (submission: PlaceOrderSubmission) => void;
}

export function useTradeModal({
  selectedSport,
  moneylineGamesForView,
  onPlaceKalshiOrder,
  onPlacePolymarketOrder,
}: UseTradeModalParams): TradeModalResult {
  const [tradeReq, setTradeReq] = useState<TradeClickRequest | null>(null);
  const [tradeError, setTradeError] = useState<string | null>(null);
  const [tradeSubmitting, setTradeSubmitting] = useState(false);

  // Compute slate min/max start times for expiry preset range
  const slateTimeRange = useMemo(() => {
    let min: string | null = null;
    let max: string | null = null;
    for (const g of moneylineGamesForView) {
      const t = g.startTimePt;
      if (!t) continue;
      if (!min || t < min) min = t;
      if (!max || t > max) max = t;
    }
    return { slateMinTime: min, slateMaxTime: max };
  }, [moneylineGamesForView]);

  // Build PlaceOrderRequest from TradeClickRequest
  const placeOrderRequest: PlaceOrderRequest | null = useMemo(() => {
    if (!tradeReq) return null;
    return {
      venue: tradeReq.venue === 'polymarket' ? 'polymarket' : 'kalshi',
      sport: selectedSport,
      marketTicker: tradeReq.marketTicker,
      tokenId: tradeReq.tokenId,
      conditionId: tradeReq.conditionId,
      tickSize: tradeReq.tickSize,
      negRisk: tradeReq.negRisk,
      side: tradeReq.side,
      priceCents: tradeReq.priceCents,
      isTakerMode: tradeReq.isTakerMode,
      teamLabel: tradeReq.teamLabel,
      gameDate: tradeReq.gameDate,
      startTimePt: tradeReq.startTimePt,
      slateMinTime: slateTimeRange.slateMinTime,
      slateMaxTime: slateTimeRange.slateMaxTime,
      defaultContracts: tradeReq.contracts,
      sweepContext: tradeReq.sweepContext,
    };
  }, [tradeReq, selectedSport, slateTimeRange]);

  const openTrade = useCallback((req: TradeClickRequest) => {
    setTradeError(null);
    setTradeReq(req);
  }, []);

  const closeTrade = useCallback(() => {
    setTradeReq(null);
  }, []);

  const handleSubmitTrade = useCallback(
    (submission: PlaceOrderSubmission) => {
      if (!tradeReq) return;

      const req = tradeReq;
      setTradeError(null);
      setTradeSubmitting(true);

      const onSuccess = () => {
        setTradeSubmitting(false);
        setTradeReq(null);
      };

      const onError = (err: unknown) => {
        setTradeSubmitting(false);
        const msg = err instanceof Error ? err.message : String(err);
        setTradeError(msg);
      };

      if (req.venue === 'kalshi') {
        if (!onPlaceKalshiOrder) {
          setTradeSubmitting(false);
          return;
        }
        onPlaceKalshiOrder({
          marketTicker: req.marketTicker,
          side: req.side,
          priceCents: submission.priceCents,
          contracts: submission.contracts,
          isTakerMode: req.isTakerMode,
          expirationTs: submission.expirationTs,
        }).then(onSuccess, onError);
      } else {
        if (!onPlacePolymarketOrder || !req.tokenId) {
          setTradeSubmitting(false);
          return;
        }
        onPlacePolymarketOrder({
          tokenId: req.tokenId,
          conditionId: req.conditionId,
          action: 'buy',
          priceCents: submission.priceCents,
          contracts: submission.contracts,
          isTakerMode: req.isTakerMode,
          tickSize: req.tickSize,
          negRisk: req.negRisk,
        }).then(onSuccess, onError);
      }
    },
    [tradeReq, onPlaceKalshiOrder, onPlacePolymarketOrder]
  );

  return {
    tradeReq,
    tradeError,
    tradeSubmitting,
    placeOrderRequest,
    isOpen: tradeReq !== null,
    openTrade,
    closeTrade,
    handleSubmitTrade,
  };
}
